<script lang="ts">
import { sentenceCase } from 'change-case'
import { defineComponent } from 'vue'

/**
 * An alert modal to show an error/success message.
 *
 * @see https://www.w3.org/WAI/ARIA/apg/patterns/alertdialog/examples/alertdialog/
 * @see https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/alert_role
 * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/dialog
 */
export default defineComponent({
  setup() {
    return {
      sentenceCase,
    }
  },
  computed: {
    alert() {
      return this.$alertStore.nextAlert
    },
  },
  methods: {
    async buttonCallback(closeModal: (forceDismissal: boolean) => void) {
      await this.alert?.callbackFn?.()

      this.$alertStore.hideAlert()
      closeModal(true)
    },
  },
})
</script>

<template>
  <AppModal
    v-if="alert"
    :id="`${alert.type}-alert`"
    :allow-dismissal="false"
    class="alert fixed inset-0 min-h-fit flex-col"
    :close-button-class="`right-4 top-4 ${alert.type === 'error' ? 'text-pure-white' : ''}`"
    :open="$alertStore.show"
    :aria-labelledby="`${alert.type}-title`"
    :aria-describedby="`${alert.type}-message`"
    aria-live="assertive"
  >
    <template #default="{ closeModal }">
      <div
        class="flex justify-center px-2 py-8"
        :class="{
          'bg-secondary': alert.type === 'success',
          'bg-primary': alert.type === 'error',
        }"
      >
        <img
          v-if="alert.type === 'error'"
          src="@/assets/images/error-icon.svg"
          title="Error Icon"
          role="presentation"
          :class="alert.type"
        />

        <AppIcon
          v-if="alert.type === 'success'"
          :icon="Icons.Member"
          :class="alert.type"
          size="lg"
        />
      </div>

      <div class="flex flex-col items-center justify-center gap-4 p-6">
        <AppBadge
          class="rounded-xl uppercase"
          size="md"
          :class="{
            'bg-secondary': alert.type === 'success',
            'bg-primary text-pure-white': alert.type === 'error',
            [alert.type]: true,
          }"
        >
          {{ sentenceCase(alert.type) }}
        </AppBadge>

        <h2
          v-if="alert.title"
          :id="`${alert.type}-title`"
          class="text-xl font-bold"
        >
          {{ alert.title }}
        </h2>

        <p :id="`${alert.type}-message`" class="text-sm">
          {{ alert.message }}
        </p>

        <AppButton
          class="button-primary w-full"
          title="Close alert dialog"
          :icon="Icons.ArrowRight"
          @click="buttonCallback(closeModal)"
        >
          {{ alert.buttonText }}
        </AppButton>
      </div>
    </template>
  </AppModal>
</template>

<style lang="scss">
.alert {
  @apply min-w-[320px];
}
</style>
